import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient, ensureCsrfCookie } from '../api/client';
import { CosmicBackground } from '../features/auth/CosmicBackground';
import { nablyudatStranitsu } from '../features/auth/browserObservation';
import { roleLabel } from '../features/auth/roleLabels';
import '../features/design-system/tokens.css';
import '../features/auth/cosmos.css';

const schema = z
  .object({
    name: z.string().min(2, 'Минимум 2 знака'),
    password: z.string().min(8, 'Минимум 8 знаков'),
    password_confirmation: z.string().min(8, 'Повторите пароль'),
    email: z.string().email('Проверьте адрес').optional().or(z.literal('')),
  })
  .refine((d) => d.password === d.password_confirmation, {
    path: ['password_confirmation'],
    message: 'Пароли не совпадают',
  });

type FormValues = z.infer<typeof schema>;

interface InvitePreview {
  account: string | null;
  role: string;
  has_phone: boolean;
  has_email: boolean;
  /** Хвост номера, по которому человек будет входить: +7 ··· ··· ·· 44 */
  phone_mask: string | null;
  email: string | null;
  expires_at: string;
}

/**
 * Приём приглашения: человек задаёт себе имя и пароль по одноразовой ссылке.
 *
 * Экран показывает, куда и в какой роли зовут, ДО ввода пароля — иначе ссылка
 * из СМС выглядит как «введите пароль неизвестно куда», и правильная реакция
 * на такое письмо — не вводить.
 */
export function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', password: '', password_confirmation: '', email: '' },
  });

  useEffect(() => {
    let alive = true;

    apiClient
      .get(`/users/invitation/${token}`)
      .then((r) => {
        if (alive) setPreview(r.data.data as InvitePreview);
      })
      .catch(() => {
        if (alive) setPreviewError('Ссылка не найдена, уже использована или просрочена.');
      })
      .finally(() => {
        if (alive) setLoadingPreview(false);
      });

    return () => {
      alive = false;
    };
  }, [token]);

  // `s698`: ссылка жива — страницу открыл браузер (а не просмотрщик ссылок
  // мессенджера: тот скрипт не исполняет). Тронули форму — отдельная отметка.
  // Номер приглашения сервер берёт из своей сессии, из адреса он сюда не идёт.
  useEffect(() => {
    if (!preview) return;
    return nablyudatStranitsu('invitation');
  }, [preview]);

  const otkazatsya = async () => {
    setError(null);
    try {
      await ensureCsrfCookie();
      await apiClient.post(`/users/decline-invite/${token}`);
      navigate('/login', { replace: true });
    } catch {
      setError('Не удалось записать отказ. Повторите позже.');
    }
  };

  const onSubmit = async (values: FormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      await ensureCsrfCookie();
      await apiClient.post(`/users/accept-invite/${token}`, {
        name: values.name,
        password: values.password,
        password_confirmation: values.password_confirmation,
        // Почту отправляем только если человек её вписал и она ещё не задана
        // в самом приглашении.
        ...(values.email && !preview?.has_email ? { email: values.email } : {}),
      });

      await qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      navigate('/', { replace: true });
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: string; errors?: Record<string, string[]> } } };
      const code = e.response?.data?.error;
      const status = e.response?.status;
      const firstFieldError = e.response?.data?.errors ? Object.values(e.response.data.errors)[0]?.[0] : undefined;

      if (code === 'expired') setError('Срок действия приглашения истёк. Попросите новое.');
      else if (code === 'already_accepted') setError('Это приглашение уже принято.');
      else if (status === 404) setError('Приглашение не найдено или ссылка повреждена.');
      else if (firstFieldError) setError(firstFieldError);
      else setError('Не удалось принять приглашение. Попробуйте позже.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="ops-skin relative min-h-screen overflow-hidden">
      <CosmicBackground />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
        <div className="ops-authcard w-full max-w-[432px]">
          <div className="mb-6 flex items-center gap-3.5">
            <img src="/logo-mark.png" alt="" aria-hidden="true" width={56} height={56} className="h-14 w-14 select-none" draggable={false} />
            <span className="text-[23px] font-bold tracking-tight text-ops-text">OpsMind</span>
          </div>

          {loadingPreview ? (
            <p className="text-ops-body text-ops-text-tertiary">Проверяем ссылку…</p>
          ) : previewError ? (
            <>
              <h1 className="mb-2 text-[22px] font-bold tracking-tight text-ops-text">Ссылка не работает</h1>
              <p className="text-ops-body text-ops-text-secondary">{previewError}</p>
              <p className="mt-4 text-ops-body text-ops-text-secondary">
                Попросите владельца прислать приглашение заново.
              </p>
            </>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <h1 className="mb-1.5 text-[22px] font-bold tracking-tight text-ops-text">
                Вас пригласили в систему
              </h1>
              <p className="mb-4 text-[15px] text-ops-text-secondary">
                {preview?.account ? `${preview.account} · ` : ''}
                роль: {roleLabel([preview!.role])}. Придумайте пароль — его будете знать только вы.
              </p>

              {/* Чем человек будет входить — сказано прямо и до полей.
                  Иначе «почта необязательна» рядом с полем почты читается как
                  «а что тогда логин?» — и человек застревает на этом вопросе. */}
              <div className="mb-5 rounded-ops-md bg-ops-primary-soft px-4 py-3 text-[13.5px] text-ops-text">
                <b className="font-semibold">Ваш логин: </b>
                {preview?.phone_mask ? (
                  <>
                    номер <b className="font-semibold">{preview.phone_mask}</b>
                  </>
                ) : preview?.email ? (
                  <b className="font-semibold">{preview.email}</b>
                ) : (
                  'почта, которую укажете ниже'
                )}
                <span className="mt-1 block text-ops-text-secondary">
                  Входить будете по нему и паролю, который сейчас придумаете.
                </span>
              </div>

              <Field label="Ваше имя" error={errors.name?.message}>
                <input className="ops-input" autoFocus disabled={submitting} {...register('name')} />
              </Field>

              {!preview?.has_email && (
                <Field
                  label={preview?.has_phone ? 'Почта — если хотите входить ещё и по ней' : 'Почта'}
                  error={errors.email?.message}
                  hint={
                    preview?.has_phone
                      ? 'Необязательно. Основной вход останется по номеру телефона.'
                      : 'По ней и будете входить.'
                  }
                >
                  <input className="ops-input" type="email" disabled={submitting} {...register('email')} />
                </Field>
              )}

              <Field label="Пароль" error={errors.password?.message}>
                <input
                  className="ops-input"
                  type="password"
                  autoComplete="new-password"
                  placeholder="минимум 8 знаков"
                  disabled={submitting}
                  {...register('password')}
                />
              </Field>

              <Field label="Повторите пароль" error={errors.password_confirmation?.message}>
                <input
                  className="ops-input"
                  type="password"
                  autoComplete="new-password"
                  disabled={submitting}
                  {...register('password_confirmation')}
                />
              </Field>

              {error && <p className="mb-3 text-[13px] text-ops-danger">{error}</p>}

              <button type="submit" className="ops-btn" disabled={submitting}>
                {submitting ? 'Создаём доступ…' : 'Принять и войти'}
              </button>

              <div className="mt-3 text-center">
                <button type="button" className="ops-link" disabled={submitting} onClick={() => void otkazatsya()}>
                  Отказаться от приглашения
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="mb-4 block">
      <span className="mb-1.5 block text-[13px] font-semibold text-ops-text-secondary">{label}</span>
      {children}
      {error ? (
        <span className="mt-1.5 block text-[13px] text-ops-danger">{error}</span>
      ) : hint ? (
        <span className="mt-1.5 block text-[13px] text-ops-text-tertiary">{hint}</span>
      ) : null}
    </label>
  );
}
